// Async build polling + artifact/log access (/v1/builds). A build is kicked off
// via workspaces.startBuild; the returned id is polled here until status is
// terminal, then artifacts and the log are fetched.

import { getJSON, request } from "./http.js";

const BASE = "/v1/builds";

/**
 * BuildStatus is the lifecycle state; terminal states are succeeded/failed/
 * stale. "queued" is a build of a repository workspace, with the data area
 * on, that waits for its checkout and a free runner. "stale" means the
 * server lost the build before it finished — it will never progress, so
 * pollers must treat it as final.
 */
export type BuildStatus = "pending" | "queued" | "running" | "succeeded" | "failed" | "stale";

/** BuildTrigger says what started a build. */
export type BuildTrigger = "manual" | "push" | "sync" | "schedule" | "seal" | "api" | "retry";

/** UndigestedSource is a data source a build read outside the sealed data. */
export interface UndigestedSource {
  kind: string;
  name: string;
}

/** BuildSecret is a secret a build ran with: never its value. */
export interface BuildSecret {
  name: string;
  scope: "workspace" | "repository" | "team";
  version: number;
}

export interface Build {
  id: string;
  workspaceId: string;
  status: BuildStatus;
  created: string;
  started: string;
  finished: string;
  /** Present (non-empty) when status is "failed". */
  error?: string;

  // Provenance: set on a queued build, which runs on a read-only checkout
  // of one commit with the sealed data; absent on the others.
  /** The commit the build ran on. */
  commit?: string;
  trigger?: BuildTrigger;
  /** The user who started the build. */
  actorId?: string;
  /** The build this one retries. */
  retryOf?: string;
  /** The build that retries this one. */
  retriedAs?: string;
  /** The bino-cli image the build ran in, and its digest. */
  image?: string;
  imageDigest?: string;
  /** sha256 over the seals the build read; empty without data. */
  sealSetDigest?: string;
  /** REPORT_PERIOD: the newest sealed month the build read. */
  reportPeriod?: string;
  /** Sources the seal set digest does not cover, so a rerun may differ. */
  undigestedSources?: UndigestedSource[];
  /** The digest of the checkout's bino.lock. */
  lockDigest?: string;
  /** The artefacts the build was limited to; empty means all. */
  artefacts?: string[];
  /** Named params the build was started with. */
  params?: Record<string, string>;
  secrets?: BuildSecret[];
  /** The start request carried env; its names and values are not stored. */
  requestEnv?: boolean;
}

export interface Artifact {
  path: string;
  size: number;
}

/** isTerminal reports whether a build will never progress (succeeded, failed, or stale). */
export function isTerminal(status: BuildStatus): boolean {
  return status === "succeeded" || status === "failed" || status === "stale";
}

/** getBuild returns the build's status + timestamps; the async-poll target (viewer+). */
export function getBuild(buildId: string): Promise<Build> {
  return getJSON<Build>(`${BASE}/${encodeURIComponent(buildId)}`);
}

/** listArtifacts returns every artifact file's path + size, sorted by path (viewer+). */
export async function listArtifacts(buildId: string): Promise<Artifact[]> {
  const { items } = await getJSON<{ items: Artifact[] }>(
    `${BASE}/${encodeURIComponent(buildId)}/artifacts`,
  );
  return items;
}

/** getArtifactBlob returns one artifact's raw bytes as a Blob (viewer+). */
export async function getArtifactBlob(buildId: string, path: string): Promise<Blob> {
  const res = await request(
    `${BASE}/${encodeURIComponent(buildId)}/artifacts/${path.split("/").map(encodeURIComponent).join("/")}`,
  );
  return res.blob();
}

/** getLog returns the captured build log text (viewer+); empty until the build runs. */
export async function getLog(buildId: string): Promise<string> {
  const res = await request(`${BASE}/${encodeURIComponent(buildId)}/log`);
  return res.text();
}
